import * as Api from '@ViewModels';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ISocialMediaConnectResult } from '../../../models/SocialMedia';
import { getMetaAuthFailureMessage } from '../../../models/utils/metaAuthFailure';
import { IModalProps } from '../Modal';
import {
	SocialMediaConnectionSuccessModal,
	SocialMediaPostTargetSelectorModal,
} from '../settings/PersonalSettings/SocialMediaSettings/presentation';

/**
 * Global provider that handles social media connection success notifications.
 * This provider monitors the URL for social media OAuth callback results and
 * displays the success modal from anywhere in the app, not just from settings.
 * It also handles post target selection when multiple options are available.
 */
export const SocialMediaConnectionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const location = useLocation<ISocialMediaConnectResult>();
	const history = useHistory();
	const userSession = useUserSession();

	// Create ViewModels for each social media type
	const vms = React.useRef<Record<string, Api.SocialMediaViewModel>>(
		Api.SupportedSocialMediaTypes.reduce<Record<string, Api.SocialMediaViewModel>>((result, x) => {
			switch (x) {
				case Api.SocialMediaType.Facebook: {
					result[x] = new Api.FacebookViewModel(userSession);
					break;
				}
				case Api.SocialMediaType.Instagram: {
					result[x] = new Api.InstagramViewModel(userSession);
					break;
				}
				case Api.SocialMediaType.LinkedIn: {
					result[x] = new Api.LinkedInViewModel(userSession);
					break;
				}
				default: {
					break;
				}
			}
			return result;
		}, {})
	).current;

	const [connectionSuccessModalProps, setConnectionSuccessModalProps] = React.useState<IModalProps>({
		isOpen: false,
		onRequestClose: Api.VmUtils.Noop,
	});

	const [postTargetModalProps, setPostTargetModalProps] = React.useState<IModalProps<Api.ISocialMediaPage>>({
		isOpen: false,
		onRequestClose: Api.VmUtils.Noop,
	});

	const [currentViewModel, setCurrentViewModel] = React.useState<Api.SocialMediaViewModel | null>(null);

	const showSuccessModal = React.useCallback(() => {
		setConnectionSuccessModalProps({
			isOpen: true,
			onRequestClose: () => {
				setConnectionSuccessModalProps({
					isOpen: false,
					onRequestClose: Api.VmUtils.Noop,
				});
			},
		});
	}, []);

	const handlePostTargetSelection = React.useCallback(
		async (result: ISocialMediaConnectResult) => {
			try {
				const vm = vms[result.type];
				if (!vm) {
					toast.error('Unsupported social media type');
					return;
				}

				const userId = result.userId;
				const filteredConnections = userSession?.user?.socialMediaConnectedAccounts.filter(
					x =>
						x?.type === vm.getConnectionType() &&
						x.encryptedBlobId &&
						x?.state !== Api.SocialMediaConnectionState.Connected &&
						(userId ? x?.userId === userId : true)
				);
				const connection = filteredConnections.length ? filteredConnections[filteredConnections.length - 1] : null;

				await vm.loadPages(connection);

				if (vm.pages.length === 0) {
					toast.error(
						'There are no post targets to choose from for the given account. Please add a page or choose a different account to connect to.'
					);
					await vm.disconnect(connection);
					return;
				}

				if (vm.pages.length === 1) {
					// Auto-select the only page
					await vm.setPostTargetPage(vm.pages[0], connection);
					showSuccessModal();
					return;
				}

				// Multiple pages - show selector modal
				setCurrentViewModel(vm);
				setPostTargetModalProps({
					isOpen: true,
					onRequestClose: async (page, cancel) => {
						if (vm.isBusy) {
							return;
						}
						setPostTargetModalProps({
							isOpen: false,
							onRequestClose: Api.VmUtils.Noop,
						});

						if (page && !cancel) {
							try {
								await vm.setPostTargetPage(page, connection);
								showSuccessModal();
							} catch (err) {
								toast.error(getMetaAuthFailureMessage(err) ?? 'Failed to set post target. Please try again.');
							}
						} else if (cancel) {
							vm.resetConnectionsToUsers(
								userSession,
								vm.connections.find(x => x.postTargetId === page?.id)
							);
						}
					},
				});
			} catch (err) {
				toast.error(getMetaAuthFailureMessage(err) ?? 'Failed to load post targets. Please try again.');
			}
		},
		[vms, userSession, showSuccessModal]
	);

	React.useEffect(() => {
		// Check if this is a social media connection result callback
		const searchParams = new URLSearchParams(location.search);

		if (searchParams.get('op') === 'connect-result') {
			// Get result from location.state if available, otherwise from query params
			let result: ISocialMediaConnectResult;

			if (location.state) {
				result = location.state as ISocialMediaConnectResult;
			} else {
				// Fallback: construct result from query params (for manual URL navigation or if state is lost)
				const success = searchParams.get('success')?.toLowerCase() === 'true';
				const errorMessage = searchParams.get('errorMessage') || undefined;
				const userId = searchParams.get('userId') || undefined;
				const type = searchParams.get('type') || undefined;

				result = {
					success,
					errorMessage,
					userId,
					type: type as Api.SocialMediaType,
				};
			}

			// Only handle post target selection if the connection was successful
			if (result.success) {
				handlePostTargetSelection(result);
			} else {
				// Show error notification for failed connections
				const errorMsg = result.errorMessage || 'Failed to connect social media account. Please try again.';
				toast.error(errorMsg);
			}

			// Clean up the URL by removing the query parameter and state
			// This prevents the modal from showing again on page refresh
			history.replace({
				pathname: location.pathname,
				search: '', // Remove all query params
				state: null,
			});
		}
	}, [location, history, handlePostTargetSelection]);

	return (
		<>
			{children}
			<SocialMediaConnectionSuccessModal modalProps={connectionSuccessModalProps} />
			{currentViewModel && (
				<SocialMediaPostTargetSelectorModal modalProps={postTargetModalProps} viewModel={currentViewModel} />
			)}
		</>
	);
};
